import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import TeamMemberProfileHeader from "./TeamMemberProfileHeader";
import { buildApiUrl } from "../utils/apiConfig";
import "../styles/team-page.css";
import AthletesTable from "./AthletesTable";
import { jsonGet } from "../utils/http";
import EditTeamMemberModal from "./EditTeamMemberModal";
import AddTaskModal from "./AddTaskModal";
import AddAthleteProfileModal from "./AddAthleteProfileModal";
import { Profile } from "./ProfileForm";
import { AthleteProfile } from "./AddAthleteProfileForm";
import { useSubscriptionLimits } from "../hooks/useSubscriptionLimits";
import SubscriptionLimitModal from "./SubscriptionLimitModal";
import { useAuth } from "../contexts/AuthContext";
import { canManageAthletes, getUserId, getUserRole, isCoach } from "../utils/auth";
import ProvisionStudentModal from "./ProvisionStudentModal";
import BulkProvisionAthletesModal from "./BulkProvisionAthletesModal";

type AdminSummary = {
    id?: number;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    telephone?: string;
    title?: string;
    companyName?: string;
    role?: string;
    avatarUrl?: string;
    state?: string;
    stateId?: number;
    sports?: Array<{ id?: number; name?: string }>;
    sportIds?: number[];
    sportId?: number;
    sportName?: string;
};

function applyAdminSummary(
    p: AdminSummary | null,
    setDisplay: (value: string) => void,
    setAdminProfile: React.Dispatch<React.SetStateAction<AdminSummary | null>>,
) {
    const d = p?.displayName || [p?.firstName, p?.lastName].filter(Boolean).join(" ");
    setDisplay(d || "");
    setAdminProfile(p || null);
}

function Spinner() {
    return (
        <div className="spinner-wrap" role="status" aria-live="polite">
            <div className="spinner" />
            <span className="sr-only">Loading…</span>
        </div>
    );
}

export default function TeamViewPage() {
    const { userId } = useParams<{ userId: string }>();

    const [display, setDisplay] = useState<string>("");
    const [adminProfile, setAdminProfile] = useState<AdminSummary | null>(null);
    const [loadingHeader, setLoadingHeader] = useState<boolean>(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const [showAddAthleteModal, setShowAddAthleteModal] = useState(false);
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [showProvisionStudentModal, setShowProvisionStudentModal] = useState(false);
    const [showBulkProvisionModal, setShowBulkProvisionModal] = useState(false);
    const [headerRefreshTrigger, setHeaderRefreshTrigger] = useState<number>(0);
    const [athletesRefreshTrigger, setAthletesRefreshTrigger] = useState<number>(0);

    const { limits, refetch: refetchLimits } = useSubscriptionLimits();
    const { authProvider } = useAuth();
    const isCleverUser = authProvider === "clever";

    const userRole = getUserRole();
    const isStaffOrAdmin = useMemo(() => {
        if (!userRole) return false;
        const lowerRole = userRole.toLowerCase();
        return (
            lowerRole === "staff" ||
            lowerRole === "admin" ||
            lowerRole === "administrator" ||
            lowerRole.includes("staff") ||
            lowerRole.includes("admin")
        );
    }, [userRole]);
    const isCleverStaffOrAdmin = isCleverUser && isStaffOrAdmin;
    const showBulkUpload = (isStaffOrAdmin && !isCleverUser) || isCleverStaffOrAdmin;
    const viewingSelf = !!userId && String(getUserId() ?? "") === String(userId);
    const canManageAthletesOnPage = canManageAthletes() && (!isCoach() || viewingSelf);

    // Build a definite string only when we have adminId
    const headerUrl = userId
        ? buildApiUrl(`api/admins/${encodeURIComponent(userId)}/profile`)
        : "";

    // Initial load only — full-page spinner while the first header fetch completes.
    useEffect(() => {
        if (!userId) {
            setLoadingHeader(true);
            setDisplay("");
            setAdminProfile(null);
            return;
        }
        let cancelled = false;
        setLoadingHeader(true);
        (async () => {
            try {
                const p = await jsonGet<AdminSummary>(headerUrl, true);
                if (!cancelled) {
                    applyAdminSummary(p, setDisplay, setAdminProfile);
                }
            } catch {
                if (!cancelled) {
                    setDisplay("");
                    setAdminProfile(null);
                }
            } finally {
                if (!cancelled) {
                    setLoadingHeader(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId, headerUrl]);

    // Soft refresh after profile save — keep the page mounted (no loadingHeader).
    useEffect(() => {
        if (!userId || !headerUrl || headerRefreshTrigger === 0) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const p = await jsonGet<AdminSummary>(headerUrl, true);
                if (!cancelled) {
                    applyAdminSummary(p, setDisplay, setAdminProfile);
                }
            } catch {
                // Keep the last known header on soft-refresh failure.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId, headerUrl, headerRefreshTrigger]);

    const handleProfileUpdated = (updatedProfile: Profile) => {
        const newDisplay = [updatedProfile.firstName, updatedProfile.lastName].filter(Boolean).join(" ");
        if (newDisplay) {
            setDisplay(newDisplay);
        }
        const maybeSports = (updatedProfile as Profile & { sports?: AdminSummary["sports"] }).sports;
        setAdminProfile((prev) => ({
            ...(prev ?? {}),
            firstName: updatedProfile.firstName,
            lastName: updatedProfile.lastName,
            email: updatedProfile.email,
            telephone: updatedProfile.telephone,
            title: updatedProfile.title,
            role: updatedProfile.role,
            companyName: updatedProfile.companyName,
            displayName: newDisplay || prev?.displayName,
            sportIds: updatedProfile.sportIds ?? prev?.sportIds,
            sports: Array.isArray(maybeSports) ? maybeSports : prev?.sports,
        }));
        setHeaderRefreshTrigger((prev) => prev + 1);
    };

    const handleAthleteAdded = (_newAthlete: AthleteProfile) => {
        setAthletesRefreshTrigger((prev) => prev + 1);
        refetchLimits();
    };

    const handleTaskCreated = () => {
        setAthletesRefreshTrigger((prev) => prev + 1);
    };

    const handleAddAthleteClick = async () => {
        // Refetch limits to get fresh data before checking
        try {
            const { fetchSubscriptionLimits, isAtAthleteLimit } = await import('../services/subscriptionLimitsService');
            const freshLimits = await fetchSubscriptionLimits();

            // Check if at limit
            if (isAtAthleteLimit(freshLimits)) {
                setShowLimitModal(true);
                return;
            }

            // Can add athlete
            setShowAddAthleteModal(true);

            // Also refetch the hook state for UI updates
            await refetchLimits();
        } catch (error) {
            // If check fails, show limit modal to be safe
            setShowLimitModal(true);
        }
    };

    const handleProvisionStudentClick = async () => {
        try {
            const { fetchSubscriptionLimits, isAtAthleteLimit } = await import('../services/subscriptionLimitsService');
            const freshLimits = await fetchSubscriptionLimits();
            if (isAtAthleteLimit(freshLimits)) {
                setShowLimitModal(true);
                return;
            }
            setShowProvisionStudentModal(true);
            await refetchLimits();
        } catch {
            setShowLimitModal(true);
        }
    };

    const handleBulkProvisionClick = async () => {
        try {
            const { fetchSubscriptionLimits, isAtAthleteLimit } = await import('../services/subscriptionLimitsService');
            const freshLimits = await fetchSubscriptionLimits();
            if (isAtAthleteLimit(freshLimits)) {
                setShowLimitModal(true);
                return;
            }
            setShowBulkProvisionModal(true);
            await refetchLimits();
        } catch {
            setShowLimitModal(true);
        }
    };

    const viewedRole = (adminProfile?.role ?? "").toUpperCase();
    const viewingCoach = viewedRole === "COACH";
    const coachSport =
        adminProfile?.sports?.find((sport) => typeof sport?.id === "number") ??
        (typeof adminProfile?.sportId === "number"
            ? { id: adminProfile.sportId, name: adminProfile.sportName }
            : undefined);
    const addAthleteInitial = {
        staffName: isCoach() && viewingSelf ? display || undefined : viewingCoach ? undefined : display || undefined,
        state: adminProfile?.state,
        stateId: adminProfile?.stateId,
        sport: coachSport?.id != null
            ? { id: coachSport.id, name: coachSport.name ?? "" }
            : undefined,
        sportName: coachSport?.name,
    };

    const isWaiting = !userId || loadingHeader;

    return (
        <section className="team-page">
            <div className="page-container">
                {isWaiting ? (
                    <Spinner />
                ) : (
                    <>
                        {/* NEW: Breadcrumbs + toolbar on one row */}
                        <div className="page-bar">
                            <nav aria-label="Breadcrumb" className="breadcrumbs">
                                <a href="/team">Team</a>
                                <span className="sep">/</span>
                                <span className="current">{display || "—"}</span>
                            </nav>

                            <div className="tm-toolbar">
                                {/* Add task button - opens modal */}
                                <button
                                    className="btn btn-red"
                                    type="button"
                                    onClick={() => setShowAddTaskModal(true)}
                                >
                                    <span className="icon" aria-hidden>
                                        +
                                    </span>
                                    <span className="text">Add task</span>
                                </button>

                                {/* Add athlete button - opens modal */}
                                {!isCleverUser && canManageAthletesOnPage && (
                                    <button
                                        className="btn btn-red"
                                        type="button"
                                        onClick={handleAddAthleteClick}
                                    >
                                        <span className="icon" aria-hidden>
                                            ＋
                                        </span>
                                        <span className="text">Add Athlete</span>
                                    </button>
                                )}
                                {isCleverStaffOrAdmin && (
                                    <button
                                        className="btn"
                                        type="button"
                                        onClick={handleProvisionStudentClick}
                                    >
                                        <span className="icon" aria-hidden>
                                            ＋
                                        </span>
                                        <span className="text">Provision student</span>
                                    </button>
                                )}
                                {showBulkUpload && (
                                    <button
                                        className="btn"
                                        type="button"
                                        onClick={handleBulkProvisionClick}
                                    >
                                        <span className="icon" aria-hidden>
                                            ＋
                                        </span>
                                        <span className="text">Bulk upload</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {headerUrl && (
                            <TeamMemberProfileHeader
                                className=""
                                loadUrl={headerUrl}
                                userId={userId}
                                onEdit={() => setShowEditModal(true)}
                                refreshTrigger={headerRefreshTrigger}
                            />
                        )}

                        <AthletesTable
                            userId={userId!}
                            refreshTrigger={athletesRefreshTrigger}
                            onAddAthleteClick={handleAddAthleteClick}
                            showProvisionStudent={isCleverStaffOrAdmin}
                            onProvisionStudentClick={handleProvisionStudentClick}
                            showBulkUpload={showBulkUpload}
                            onBulkUploadClick={handleBulkProvisionClick}
                        />
                    </>
                )}

                {/* Edit Team Member Modal */}
                {userId && (
                    <EditTeamMemberModal
                        open={showEditModal}
                        onClose={() => setShowEditModal(false)}
                        onSaved={handleProfileUpdated}
                        userId={userId}
                    />
                )}

                {/* Add Task Modal */}
                {userId && (
                    <AddTaskModal
                        open={showAddTaskModal}
                        onClose={() => setShowAddTaskModal(false)}
                        onTaskCreated={handleTaskCreated}
                    />
                )}

                {/* Add Athlete Modal */}
                <AddAthleteProfileModal
                    open={showAddAthleteModal}
                    onClose={() => setShowAddAthleteModal(false)}
                    onSaved={handleAthleteAdded}
                    parentUserId={userId}
                    initial={addAthleteInitial}
                />

                {/* Subscription Limit Modal */}
                <SubscriptionLimitModal
                    open={showLimitModal}
                    onClose={() => setShowLimitModal(false)}
                    limits={limits}
                />

                <ProvisionStudentModal
                    open={showProvisionStudentModal}
                    onClose={() => setShowProvisionStudentModal(false)}
                    onProvisioned={() => {
                        setAthletesRefreshTrigger((prev) => prev + 1);
                        refetchLimits();
                    }}
                    onAthleteLimitReached={() => {
                        setShowProvisionStudentModal(false);
                        setShowLimitModal(true);
                    }}
                />

                <BulkProvisionAthletesModal
                    open={showBulkProvisionModal}
                    onClose={() => setShowBulkProvisionModal(false)}
                    onProvisioned={() => {
                        setAthletesRefreshTrigger((prev) => prev + 1);
                        refetchLimits();
                    }}
                    templateStaffEmail={adminProfile?.email}
                />
            </div>
        </section>
    );
}